import { useCallback, useEffect, useRef, useState } from 'react';

/** A session that survives a refresh but not a closed tab. An internal tool should not linger. */
export function useSession<T>(key: string): [T | null, (v: T | null) => void] {
  const [value, setValue] = useState<T | null>(() => {
    try { return JSON.parse(sessionStorage.getItem(key) ?? 'null') as T | null; }
    catch { return null; }
  });
  const save = useCallback((v: T | null) => {
    if (v) sessionStorage.setItem(key, JSON.stringify(v));
    else sessionStorage.removeItem(key);
    setValue(v);
  }, [key]);
  return [value, save];
}

/** Loads once, exposes a reload that keeps showing the last data, never leaves the caller guessing. */
export function useResource<T>(loader: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);
  const loaded = useRef(false);

  // `loading` means "nothing to show yet", so only the first load sets it. A
  // reload after a save used to set it too: the screen swapped to a spinner, and
  // in the shell — which waits on the task list — every screen unmounted, then
  // remounted and fetched again. A new property or task showed up only after
  // two or three rounds of spinners. Now the old data stays until the new lands.
  const reload = useCallback(async () => {
    if (!loaded.current) setLoading(true);
    setError(null);
    try {
      const next = await loader();
      if (alive.current) { setData(next); loaded.current = true; }
    } catch (e) {
      if (alive.current) setError(e as Error);
    } finally {
      if (alive.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    alive.current = true;
    // different inputs (another account signed in): start again from nothing
    loaded.current = false;
    setData(null);
    void reload();
    return () => { alive.current = false; };
  }, [reload]);

  return { data, error, loading, reload };
}

export function useToast() {
  const [toast, setToast] = useState<{ title: string; detail?: string } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback((title: string, detail?: string) => {
    setToast({ title, detail });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 4200);
  }, []);
  return [toast, show] as const;
}
